// Makes it easy to draw SVGs over an image, with full support for zoom & pan.

import { ReactEventHandler, forwardRef, useState } from 'react';
import { useCallback } from 'react';
import type { TextSearchResultType } from 'packages/lib/api/logminer/contract/data-discovery-text-search-results';
import styled from '@emotion/styled';
import classNames from 'classnames';

const AnnotationRectSVG = styled.svg`
  rect:hover {
    opacity: 0.2;
  }
  rect {
    fill: transparent;
  }
`;

const styles: Record<string, React.CSSProperties> = {
  svgWrapper: {
    width: '100%',
    height: '100%',
    position: 'absolute',
    top: 0,
    left: 0,
  },
};

export interface AnnotatedImageProps {
  src?: string;
  annotationsRed?: TextSearchResultType['bounding_box'];
  annotationsBlue?: TextSearchResultType['bounding_box'];
  className?: string;
  width?: string | number;
  height?: string | number;
  zoom?: number;
  style?: React.CSSProperties;
}
export const AnnotatedImage = forwardRef<SVGSVGElement, AnnotatedImageProps>(
  (
    {
      src,
      annotationsRed,
      annotationsBlue,
      className,
      width,
      height,
      zoom,
      style,
    }: AnnotatedImageProps,
    ref
  ): JSX.Element => {
    const [canvasWidth, setCanvasWidth] = useState(600);
    const [canvasHeight, setCanvasHeight] = useState(600);

    const imgOnLoad: ReactEventHandler<HTMLImageElement> = useCallback((e) => {
      const image = e.currentTarget;
      setCanvasWidth(image.naturalWidth);
      setCanvasHeight(image.naturalHeight);
    }, []);

    return (
      <>
        <img
          src={src}
          alt={src}
          onLoad={imgOnLoad}
          style={{ maxHeight: '100vh', ...style }} // css 'object-fit: contain doesn't work to contain this image. maxHeight here does
        />

        <div style={styles.svgWrapper}>
          <AnnotationRectSVG
            className={classNames('annotated-image', className)}
            preserveAspectRatio="xMinYMin meet"
            ref={ref}
            viewBox={`0 0 ${canvasWidth} ${canvasHeight}`}
          >
            {annotationsRed?.map(
              ({ top_2d, left_2d, width_2d, height_2d }, idx) => {
                return (
                  <rect
                    key={idx}
                    x={left_2d}
                    y={top_2d}
                    width={width_2d}
                    height={height_2d}
                    strokeWidth={`${zoom ? 12 / (zoom + 0.5) : 8}px`}
                    stroke="red"
                  />
                );
              }
            )}
            {annotationsBlue?.map(
              ({ top_2d, left_2d, width_2d, height_2d }, idx) => {
                return (
                  <rect
                    key={idx}
                    x={left_2d}
                    y={top_2d}
                    width={width_2d}
                    height={height_2d}
                    strokeWidth={`${zoom ? 12 / (zoom + 0.5) : 8}px`}
                    stroke="blue"
                  />
                );
              }
            )}
          </AnnotationRectSVG>
        </div>
      </>
    );
  }
);
